import { useCallback } from 'react'
import { logMessage } from 'utils/logMessage'
import useConfirmTransactionModal from 'components/widgets/ConfirmTransactionModal/useConfirmTransactionModal'
import useToast from 'hooks/useToast'
import { useTranslation } from 'react-i18next'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { BigNumber, ContractTransaction } from 'ethers'
import { useGetMproPrice } from 'hooks/useGetMproPrice'
import { useLifetimeTokens, useRewardProxyContractAddress } from 'state/global/hooks'
import { getMproRewardProxyContract } from 'utils/contractHelpers'
import { useWeb3ProviderAndContract } from 'hooks/useWeb3ProviderAndContract'
import { useSwitchChainForContractTx } from 'hooks/useSwitchChainForContractTx'

export interface Reward {
  _rewardToClaim: BigNumber
  _claimedReward: BigNumber
  _claimRewardFee: BigNumber
  _lastRewardTimestamp: BigNumber
}

export interface SingleClaim {
  _walletAddress: string
  _rewardAmount: BigNumber
  _timestamp: BigNumber
}

export const useMproRewardProxyContract = (): {
  getReward: (address: string, chainId: number) => Promise<Reward>
  claimReward: (chainId: number) => Promise<ContractTransaction>
  getWalletRewardUpdates: (address: string, chainId: number) => Promise<SingleClaim[]>
  getMinUpdateTokenAmount: (chainId: number) => Promise<BigNumber>
  /** What one mint adds to the fee paid at claim time, in wei. */
  getMintFee: (chainId: number) => Promise<BigNumber>
} => {
  const { account } = useWeb3React()
  const rewardProxyContractAddresses = useRewardProxyContractAddress()
  const { getProviderOrSignerByChainId } = useWeb3ProviderAndContract()
  const ensureChainForTx = useSwitchChainForContractTx()

  const getReward = useCallback(
    async (address: string, chainId: number): Promise<Reward> => {
      const rewardProxyContractAddress = rewardProxyContractAddresses.find((contract) => contract.chainId === chainId)
      const RewardProxyContract = getMproRewardProxyContract(
        rewardProxyContractAddress?.address,
        getProviderOrSignerByChainId(rewardProxyContractAddress?.chainId, account)
      )
      return await RewardProxyContract.getReward(address)
    },
    [account, rewardProxyContractAddresses]
  )

  const claimReward = useCallback(
    async (proxyChainId: number): Promise<ContractTransaction> => {
      const onCorrectChain = await ensureChainForTx(proxyChainId)
      if (!onCorrectChain) {
        throw new Error('Network switch required')
      }
      const rewardProxyContractAddress = rewardProxyContractAddresses.find(
        (contract) => contract.chainId === proxyChainId,
      )
      const RewardProxyContract = getMproRewardProxyContract(
        rewardProxyContractAddress?.address,
        getProviderOrSignerByChainId(rewardProxyContractAddress?.chainId, account),
      )
      const reward = await RewardProxyContract.getReward(account)
      return await RewardProxyContract.claimReward({
        from: account,
        value: reward._claimRewardFee,
      })
    },
    [account, rewardProxyContractAddresses, ensureChainForTx, getProviderOrSignerByChainId],
  )

  const getWalletRewardUpdates = useCallback(
    async (address: string, chainId: number): Promise<SingleClaim[]> => {
      const rewardProxyContractAddress = rewardProxyContractAddresses.find((contract) => contract.chainId === chainId)
      const RewardProxyContract = getMproRewardProxyContract(
        rewardProxyContractAddress?.address,
        getProviderOrSignerByChainId(rewardProxyContractAddress?.chainId, account)
      )
      return await RewardProxyContract.getClaimHistory(address)
    },
    [account, rewardProxyContractAddresses]
  )

  const getMinUpdateTokenAmount = useCallback(
    async (chainId: number): Promise<BigNumber> => {
      const rewardProxyContractAddress = rewardProxyContractAddresses.find((contract) => contract.chainId === chainId)
      const RewardProxyContract = getMproRewardProxyContract(
        rewardProxyContractAddress?.address,
        getProviderOrSignerByChainId(rewardProxyContractAddress?.chainId, account)
      )
      const minUpdateTokenAmount = await RewardProxyContract.minUpdateTokenAmount()
      return minUpdateTokenAmount
    },
    [account, rewardProxyContractAddresses]
  )

  /**
   * What one mint adds to the fee paid at claim time, in wei.
   *
   * The contract is the only honest source for this: the interface used to
   * quote "$5 to $20 in ETH", a range in the wrong currency that nobody could
   * check and that goes stale the moment the operator changes the fee.
   */
  const getMintFee = useCallback(
    async (chainId: number): Promise<BigNumber> => {
      const rewardProxyContractAddress = rewardProxyContractAddresses.find(
        (contract) => contract.chainId === chainId
      )
      const RewardProxyContract = getMproRewardProxyContract(
        rewardProxyContractAddress?.address,
        getProviderOrSignerByChainId(rewardProxyContractAddress?.chainId, account)
      )
      return RewardProxyContract.updateRewardNativeFee()
    },
    [account, rewardProxyContractAddresses]
  )

  return {
    getReward,
    claimReward,
    getWalletRewardUpdates,
    getMinUpdateTokenAmount,
    getMintFee
  }
}
